import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, Shield, Sparkles, Circle, Lock, Check, Trophy, HandCoins, Crown } from "lucide-react";
import { Sigil } from "@/lib/cosmetics";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { isMobileBattleViewport, mobileBattleToastPosition } from "@/lib/toastPosition";
import { PageHeader, Loader, Empty, btn, Pagination } from "@/components/Page";
import Modal from "@/components/Modal";
import BattleReplay from "@/components/BattleReplay";
import QuestPanel from "@/components/QuestPanel";
import { TACTICS, CLASS_META, RARITY } from "@/lib/game";
import { useAuth } from "@/context/AuthContext";
import { CombatGuideButton } from "@/components/CombatGuide";
import GoldenCrownIcon from "@/components/GoldenCrownIcon";
import { ArenaComparisonModal } from "@/components/ArenaComparisonModal";

// Custom Arena Icon
const ArenaIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/arena-icon.png" 
    alt="Arena" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

function SalvageChips({ rew, dailyProgress }) {
  if (!rew) return null;
  const rows = [];
  for (const kind of ["weapon", "armor"]) {
    const tally = rew[kind] || {};
    for (const [rarity, count] of Object.entries(tally)) {
      if (count) rows.push({ kind, rarity, count });
    }
  }
  const hasFragments = rows.length > 0;
  const hasFollis = rew.follis && rew.follis > 0;
  const fragmentTotal = rows.reduce((total, row) => total + row.count, 0);
  const dailyBaseFragments = rew.daily_arena_fragment_bonus ? fragmentTotal / 2 : 0;
  
  if (!hasFragments && !hasFollis) return null;
  
  return (
    <div data-testid="salvage-rewards" className="mt-3 border border-emerald-900/40 bg-emerald-950/10 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 flex items-center gap-1.5 mb-1.5"><Sparkles size={11} /> Battle Rewards</div>
      {rew.daily_arena_fragment_bonus && (
        <p data-testid="daily-double-fragments-received" className="mb-2 text-[11px] text-amber-200">
          Daily double applied: {dailyBaseFragments} base fragment{dailyBaseFragments === 1 ? "" : "s"} doubled to {fragmentTotal} total fragments received this bout.
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {rows.map((r, i) => {
          const meta = RARITY[r.rarity] || RARITY.common;
          return (
            <span key={i} className={`text-[11px] px-1.5 py-0.5 border rounded-sm ${meta.text} ${meta.border} ${meta.bg}`}>
              +{r.count} {r.rarity} {r.kind} frag{r.count > 1 ? "s" : ""}
            </span>
          );
        })}
        {hasFollis && (
          <span className="text-[11px] px-1.5 py-0.5 border rounded-sm text-amber-300 border-amber-900/50 bg-amber-950/20">
            +{rew.follis} Follis
          </span>
        )}
      </div>
      {dailyProgress && (
        <p data-testid="daily-double-fragments-result-progress" className="mt-2 border-t border-emerald-900/30 pt-2 text-[11px] text-zinc-400">
          Daily double fragments: <span className="font-semibold text-amber-300">{dailyProgress.used}/{dailyProgress.cap}</span> outgoing challenges today · {dailyProgress.remaining} bonus bout{dailyProgress.remaining === 1 ? "" : "s"} remaining
        </p>
      )}
    </div>
  );
}

function ProlusioReward({ claim, granted, newlyUnlocked }) {
  if (!claim) return null;
  
  const reward = granted || claim.reward;
  if (!reward) return null;

  return (
    <div data-testid="prolusio-reward-granted" className="border border-emerald-900/40 bg-emerald-950/10 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 flex items-center gap-1.5 mb-2"><Trophy size={11} /> Victory Spoils</div>
      <div className="space-y-2">
        {reward.denarii && (
          <div className="text-sm text-emerald-300 flex items-center gap-1.5">
            <HandCoins size={14} /> +{reward.denarii} denarii added to your campaign purse
          </div>
        )}
        {reward.fragments && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400/70 mb-1">Fragments earned:</div>
            <SalvageChips rew={reward.fragments} />
          </div>
        )}
      </div>
      {newlyUnlocked && <div className="text-[11px] text-sky-300 mt-2 flex items-center gap-1.5"><Sparkles size={11} /> A new challenger has entered the Prolusio ladder!</div>}
    </div>
  );
}

const STATUS_COLOR = {
  pending: "text-amber-400 border-amber-700/50",
  accepted: "text-sky-400 border-sky-800/50",
  completed: "text-emerald-400 border-emerald-800/50",
  declined: "text-orange-400 border-orange-800/50",
  expired: "text-zinc-500 border-zinc-700",
};

function TacticsPicker({ value, onChange, gladiator }) {
  const preferredTactic = gladiator?.traits?.fighting_style;
  const hasTrait = !!preferredTactic;

  return (
    <div className="my-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-zinc-500">Choose Tactics</span>
        <CombatGuideButton />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {TACTICS.map((t) => {
          const isPreferred = hasTrait && t.id === preferredTactic;
          return (
            <button key={t.id} type="button" data-testid={`tactic-${t.id}`} onClick={() => onChange(t.id)}
              className={`px-2 py-2 text-xs border text-center transition-colors relative ${
                value === t.id 
                  ? "border-amber-500 bg-amber-900/20 text-amber-300" 
                  : isPreferred
                  ? "border-violet-500/60 bg-violet-950/20 text-violet-300 hover:border-violet-500"
                  : "border-zinc-800 text-zinc-400 hover:border-amber-700/50"
              }`}>
              {isPreferred && (
                <span data-testid={`preferred-tactic-badge-${t.id}`} className="absolute -top-1 -right-1 text-[9px] px-1 py-0.5 bg-violet-600 text-white rounded-sm font-semibold uppercase tracking-wider">★</span>
              )}
              <div className="font-cinzel uppercase tracking-wide">{t.label}</div>
              <div className="text-[10px] text-zinc-600 mt-0.5">{t.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Pager({ page, total, perPage, onPage, testid }) {
  const pages = Math.ceil(total / perPage);
  return <Pagination page={page} totalPages={pages} onChange={onPage} testid={testid} className="mt-3" />;
}

export default function Challenges() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [autoAccept, setAutoAccept] = useState(false);
  const [autoTactics, setAutoTactics] = useState("balanced");
  const [confirmAuto, setConfirmAuto] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [opponents, setOpponents] = useState([]);
  const [range, setRange] = useState(50);
  const [myAllianceId, setMyAllianceId] = useState(null);
  const [myRivalId, setMyRivalId] = useState(null);
  const [lowThreshold, setLowThreshold] = useState(950);
  const [lowCap, setLowCap] = useState(1000);
  const [opponentBracket, setOpponentBracket] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [myGlad, setMyGlad] = useState("");
  const [oppGlad, setOppGlad] = useState("");
  const [tactics, setTactics] = useState("balanced");
  const [arenaRivals, setArenaRivals] = useState({});
  useEffect(() => {
    if (!myGlad) {
      setArenaRivals({});
      fetchOpponents(1, true, "");
      return;
    }
    api.get(`/gladiators/${myGlad}/rivals`)
      .then((r) => { const m = {}; (r.data.rivals || []).forEach((rv) => { m[rv.opponent_id] = rv; }); setArenaRivals(m); })
      .catch(() => setArenaRivals({}));

    // Fetch opponents specifically for this selected gladiator!
    fetchOpponents(1, true, myGlad);
  }, [myGlad, tactics]);
  const [busy, setBusy] = useState(false);
  const [acceptCh, setAcceptCh] = useState(null);
  const [accTactics, setAccTactics] = useState("balanced");
  const [battle, setBattle] = useState(null);
  const [limits, setLimits] = useState(null);
  const [dailyFragmentBonus, setDailyFragmentBonus] = useState(null);
  const [scrimOpen, setScrimOpen] = useState(false);
  const [scrimMode, setScrimMode] = useState("self");
  const [prolusio, setProlusio] = useState(null);
  const [pGlad, setPGlad] = useState("");
  const [pTier, setPTier] = useState(null);
  const [pTactics, setPTactics] = useState("balanced");
  const [pClaim, setPClaim] = useState(null);
  const [pUnlocked, setPUnlocked] = useState(false);
  const [battleInlineNotices, setBattleInlineNotices] = useState([]);
  const [sg1, setSg1] = useState("");
  const [sg2, setSg2] = useState("");
  const [st1, setSt1] = useState("balanced");
  const [st2, setSt2] = useState("aggressive");
  const [friends, setFriends] = useState([]);
  const [friendlyScrims, setFriendlyScrims] = useState({ incoming: [], outgoing: [] });
  const [fsGlad, setFsGlad] = useState("");
  const [fsFriend, setFsFriend] = useState("");
  const [fsTactics, setFsTactics] = useState("balanced");
  const [acceptFS, setAcceptFS] = useState(null);
  const [afGlad, setAfGlad] = useState("");
  const [afTactics, setAfTactics] = useState("defensive");
  const [inPage, setInPage] = useState(1);
  const [outPage, setOutPage] = useState(1);
  const [opponentsPage, setOpponentsPage] = useState(1);
  const [hasMoreOpponents, setHasMoreOpponents] = useState(false);
  const [loadingOpponents, setLoadingOpponents] = useState(false);
  const PER_PAGE = 10;

  const orderedIncomingChallenges = useMemo(() => {
    if (!challenges?.incoming) return [];
    const rank = {
      pending: 0,
      accepted: 1,
      declined: 2,
      completed: 3,
      expired: 4,
    };
    return [...challenges.incoming].sort((a, b) => {
      const left = rank[a.status] ?? 99;
      const right = rank[b.status] ?? 99;
      if (left !== right) return left - right;
      const leftTime = Date.parse(a.created_at || a.completed_at || a.expired_at || 0) || 0;
      const rightTime = Date.parse(b.created_at || b.completed_at || b.expired_at || 0) || 0;
      return rightTime - leftTime;
    });
  }, [challenges]);

  const fetchOpponents = async (p = 1, replace = false, gladId = "") => {
    setLoadingOpponents(true);
    try {
      const q = gladId ? `&glad_id=${gladId}&tactics=${tactics}` : "";
      const { data } = await api.get(`/opponents?page=${p}&limit=20${q}`);
      if (replace) {
        setOpponents(data.opponents);
      } else {
        setOpponents((prev) => [...prev, ...data.opponents]);
      }
      setOpponentsPage(p);
      setHasMoreOpponents(!!data.has_more);
      setRange(data.challenge_range || 50);
      setMyAllianceId(data.my_alliance_id || null);
      setMyRivalId(data.my_rival_id || null);
      setLowThreshold(data.low_rated_threshold ?? 950);
      setLowCap(data.low_rated_cap ?? 1000);
      setOpponentBracket(data.elo_division || null);
    } catch (e) {
      toast.error("Failed to load opponents list");
    } finally {
      setLoadingOpponents(false);
    }
  };

  const handleLoadMoreOpponents = () => {
    if (loadingOpponents || !hasMoreOpponents) return;
    fetchOpponents(opponentsPage + 1, false, myGlad);
  };

  const load = async () => {
    // Phase 1: Load main gladiator & challenge data first to render page instantly!
    const [g, c, l, fr, fs, bonus] = await Promise.all([
      api.get("/gladiators"),
      api.get("/challenges"),
      api.get("/limits"),
      api.get("/friends"),
      api.get("/scrimmage/friendly"),
      api.get("/arena/daily-fragment-bonus").catch(() => ({ data: null })),
    ]);
    setMine(g.data.active.filter((x) => !x.auctioned));
    setChallenges(c.data);
    setLimits(l.data);
    setFriends(fr.data.friends || []);
    setFriendlyScrims(fs.data || { incoming: [], outgoing: [] });
    setDailyFragmentBonus(bonus.data);
    
    // Phase 2: Trigger opponents fetching asynchronously afterward!
    fetchOpponents(1, true, "");
  };
  useEffect(() => { load().catch(() => setChallenges({ incoming: [], outgoing: [] })); }, []);

  const send = async () => {
    if (!myGlad || !oppGlad) return toast.error("Pick your fighter and an opponent");
    setBusy(true);
    try {
      const { data } = await api.post("/challenges", { my_gladiator_id: myGlad, opponent_gladiator_id: oppGlad, tactics });
      setNewOpen(false); setMyGlad(""); setOppGlad("");
      if (data.auto_accepted && data.battle_id) {
        if (isMobileBattleViewport()) {
          setBattleInlineNotices([{ id: `auto-accepted-${data.battle_id}`, tone: "success", message: "Auto-accepted! Showing battle report." }]);
        } else {
          toast.success("Auto-accepted! Showing battle...", { position: mobileBattleToastPosition() });
        }
        if (data.battle) {
          setBattle(data.battle);
        } else {
          await viewBattle(data.battle_id);
        }
      } else {
        toast.success("Challenge sent to opponent's inbox");
      }
      load().catch(() => {});
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const openComparison = async () => {
    if (!myGlad || !oppGlad) return;
    setComparisonOpen(true);
    setComparisonLoading(true);
    setComparison(null);
    try {
      const { data } = await api.get(`/arena/comparison?my_gladiator_id=${myGlad}&opponent_gladiator_id=${oppGlad}`);
      setComparison(data);
    } catch (e) {
      toast.error(apiErr(e, "Unable to prepare comparison."));
    } finally {
      setComparisonLoading(false);
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/challenges/${acceptCh.id}/accept`, { tactics: accTactics });
      setAcceptCh(null); setBattle(data); await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const decline = async (id) => {
    try { await api.post(`/challenges/${id}/decline`); toast.success("Challenge declined"); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const viewBattle = async (battleId) => {
    try { const { data } = await api.get(`/battles/${battleId}`); setBattle(data); } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    if (!scrimOpen || scrimMode !== "prolusio") return;
    const url = pGlad ? `/prolusio?gladiator_id=${pGlad}` : "/prolusio";
    api.get(url).then(({ data }) => setProlusio(data)).catch((e) => toast.error(apiErr(e)));
  }, [scrimOpen, scrimMode, pGlad]);

  const runProlusio = async () => {
    if (!pGlad) return toast.error("Pick your gladiator");
    if (!pTier) return toast.error("Pick an opponent");
    setBusy(true);
    try {
      const { data } = await api.post("/prolusio", { gladiator_id: pGlad, tier: pTier, tactics: pTactics });
      setScrimOpen(false);
      setPClaim(data.claim || null);
      setPUnlocked(!!data.newly_unlocked);
      setBattle({ ...data, __prolusio: true });
      // Refresh user to update denarii and fragments
      await refreshUser();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const runScrimmage = async () => {
    if (!sg1 || !sg2) return toast.error("Pick two of your gladiators");
    if (sg1 === sg2) return toast.error("Pick two different gladiators");
    setBusy(true);
    try {
      const { data } = await api.post("/scrimmage", { g1_id: sg1, g1_tactics: st1, g2_id: sg2, g2_tactics: st2 });
      setScrimOpen(false); setBattle(data);
      if (typeof data.scrimmages_remaining === "number") setLimits((p) => ({ ...(p || {}), scrimmages_remaining: data.scrimmages_remaining }));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const sendFriendly = async () => {
    if (!fsGlad) return toast.error("Pick one of your gladiators");
    if (!fsFriend) return toast.error("Pick a friend to challenge");
    setBusy(true);
    try {
      const { data } = await api.post("/scrimmage/friendly", { opponent_user: fsFriend, my_gladiator_id: fsGlad, tactics: fsTactics });
      toast.success("Friendly scrimmage sent — waiting for your friend to accept");
      setScrimOpen(false); setFsGlad(""); setFsFriend("");
      if (typeof data.scrimmages_remaining === "number") setLimits((p) => ({ ...(p || {}), scrimmages_remaining: data.scrimmages_remaining }));
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const acceptFriendly = async () => {
    if (!afGlad) return toast.error("Pick your gladiator");
    setBusy(true);
    try {
      const { data } = await api.post(`/scrimmage/friendly/${acceptFS.id}/accept`, { gladiator_id: afGlad, tactics: afTactics });
      setAcceptFS(null); setAfGlad(""); setBattle(data); await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const declineFriendly = async (id) => {
    try { await api.post(`/scrimmage/friendly/${id}/decline`); toast.success("Friendly scrimmage declined"); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => { setAutoAccept(!!user?.auto_accept_challenges); }, [user?.auto_accept_challenges]);
  useEffect(() => { setAutoTactics(user?.auto_accept_tactics || "balanced"); }, [user?.auto_accept_tactics]);

  const saveAutoAccept = async (enabled) => {
    setAutoBusy(true);
    try {
      await api.post("/settings/auto-accept", { enabled, tactics: autoTactics });
      setAutoAccept(enabled);
      setConfirmAuto(false);
      await refreshUser();
      toast.success(enabled ? `Auto-accept enabled (${autoTactics}) — challenges will resolve instantly.` : "Auto-accept disabled.");
    } catch (e) { toast.error(apiErr(e)); } finally { setAutoBusy(false); }
  };

  const changeAutoTactics = async (t) => {
    setAutoTactics(t);
    if (!autoAccept) return;   // staged for when they enable; only persist while already on
    try {
      await api.post("/settings/auto-accept", { enabled: true, tactics: t });
      await refreshUser();
      toast.success(`Auto-accept tactics set to ${t}.`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleAutoAccept = () => {
    if (autoAccept) saveAutoAccept(false);   // turning OFF is immediate
    else setConfirmAuto(true);               // turning ON asks for confirmation
  };

  if (challenges === null) return <Loader />;

  const myFighter = mine.find((g) => g.id === myGlad);
  const myRating = myFighter?.rating;
  const showMatchupGuidance = Number(myRating) < 1300;
  const isLowRated = myRating != null && myRating < lowThreshold;
  
  // Filter eligible opponents and sort by priority
  const eloDivision = (rating) => (rating <= 1300 ? "bronze" : rating <= 1999 ? "silver" : "golden");
  const selectedDivision = opponentBracket || (myFighter ? eloDivision(myFighter.rating || 0) : null);
  const displayDivision = selectedDivision === "golden" ? "gold" : selectedDivision;
  const eligible = myFighter
    ? opponents.filter((g) => g.elo_division === selectedDivision)
        .filter((o) => {
          const isCrownHolder = o.bronze_crown || o.silver_crown || o.golden_crown;
          const higherRatedCrownException = isCrownHolder && myRating > o.rating;
          return Math.abs(o.rating - myRating) <= range || (isLowRated && o.rating <= lowCap) || higherRatedCrownException || o.recovery_match;
        })
        .sort((a, b) => {
          const getTier = (g) => {
            if (g.owner_auto_accept && g.fight_ready) return 0;
            if (g.owner_auto_accept) return 1;
            if (g.fight_ready) return 2;
            return 3;
          };
          const tierDiff = getTier(a) - getTier(b);
          if (tierDiff !== 0) return tierDiff;
          return (a.rating ?? 9999) - (b.rating ?? 9999);
        })
    : [];

  return (
    <div>
      {/* Arena Challenges Banner */}
      <div className="relative mb-6 overflow-hidden border border-red-900/40 rounded-sm">
        <img 
          src="/arena-challenges-banner.png" 
          alt="The Arena - Colosseum Challenges" 
          className="w-full h-44 sm:h-60 object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
        <h2 className="absolute bottom-3 left-5 font-cinzel text-2xl sm:text-3xl text-red-200 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] flex items-center gap-2">
          <Swords size={26} /> The Arena
        </h2>
      </div>

      <PageHeader title="The Arena" subtitle={<span className="block max-w-[28rem]">Send challenges; opponents must accept before the auto-sim resolves.</span>} icon={ArenaIcon}
        action={
          <div className="w-full sm:w-auto space-y-2">
            <div data-testid="daily-double-fragments-bar" className="border border-amber-800/60 bg-amber-950/20 px-3 py-2 text-left">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="font-cinzel text-[11px] uppercase tracking-wide text-amber-200">Daily double fragments</span>
                <span data-testid="daily-double-fragments-count" className="text-sm font-semibold tabular-nums text-amber-300">{dailyFragmentBonus?.used ?? "…"}/{dailyFragmentBonus?.cap ?? 5} outgoing today</span>
              </div>
              <p data-testid="daily-double-fragments-copy" className="mt-1 text-[11px] text-zinc-400">Your first five outgoing ranked challenges reward 2× fragments. Resets at midnight server time.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <button
                data-testid="scrimmage-btn"
                className={btn("ghost") + " w-full sm:w-auto text-center px-4 sm:px-5"}
                onClick={() => setScrimOpen(true)}
              >
                Scrimmage
              </button>
              <button
                data-testid="new-challenge-btn"
                className={btn() + " recruit-pulse w-full sm:w-auto text-center px-4 sm:px-5"}
                onClick={() => setNewOpen(true)}
              >
                <span className="sm:hidden">Send Challenge</span>
                <span className="hidden sm:inline">Enter the Arena: Send Challenge!</span>
              </button>
            </div>
          </div>
        } />

      <div data-testid="auto-accept-bar" className="mb-6 px-4 py-3 border border-zinc-800 bg-zinc-950/60 rounded-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <Shield size={16} className={autoAccept ? "text-emerald-400 mt-0.5" : "text-zinc-500 mt-0.5"} />
            <div>
              <p className="text-sm text-zinc-200 font-semibold">Auto-accept challenges</p>
              <p className="text-xs text-zinc-500">When on, any challenge sent to you resolves instantly using your chosen tactics — as long as that gladiator isn't resting.</p>
            </div>
          </div>
          <button data-testid="auto-accept-toggle" onClick={toggleAutoAccept} disabled={autoBusy}
            role="switch" aria-checked={autoAccept}
            className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${autoAccept ? "bg-emerald-500" : "bg-zinc-700"} disabled:opacity-50`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${autoAccept ? "translate-x-6" : ""}`} />
          </button>
        </div>
        {autoAccept && (
          <div className="mt-3 pt-3 border-t border-zinc-800" data-testid="auto-accept-tactics">
            <p className="text-[11px] text-zinc-500 mb-2">Default tactics used by all of your gladiators, unless other tactics set on their profile from your active roster.</p>
            <div className="flex flex-wrap gap-2">
              {TACTICS.map((t) => (
                <button key={t.id} data-testid={`auto-tactic-${t.id}`} onClick={() => changeAutoTactics(t.id)}
                  className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${autoTactics === t.id ? "border-emerald-500 bg-emerald-900/20 text-emerald-300" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                  <span className="font-cinzel uppercase tracking-wide">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <QuestPanel onClaimed={refreshUser} />

      {confirmAuto && (
        <Modal open onClose={() => setConfirmAuto(false)} title="Enable auto-accept?">
          <div data-testid="auto-accept-confirm">
            <p className="text-sm text-zinc-300">
              With auto-accept on, <span className="text-amber-300">every challenge sent to you will be fought immediately</span>. You can choose a tactic for each gladiator by viewing them in the active roster or choose a general tactic for them all below. Your gladiator must not be resting and you cannot decline. Continue?
            </p>
            <p data-testid="auto-accept-protect-note" className="text-xs text-sky-300/90 mt-2 flex items-start gap-1.5">
              <Shield size={13} className="mt-0.5 shrink-0" /> You can protect a specific fighter from auto-accept in your active roster.
            </p>
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Tactics for all gladiators</p>
              <div className="flex flex-wrap gap-2">
                {TACTICS.map((t) => (
                  <button key={t.id} data-testid={`auto-confirm-tactic-${t.id}`} onClick={() => setAutoTactics(t.id)}
                    className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${autoTactics === t.id ? "border-emerald-500 bg-emerald-900/20 text-emerald-300" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                    <span className="font-cinzel uppercase tracking-wide">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button data-testid="auto-accept-cancel" onClick={() => setConfirmAuto(false)} disabled={autoBusy} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm">Cancel</button>
              <button data-testid="auto-accept-confirm-btn" onClick={() => saveAutoAccept(true)} disabled={autoBusy} className={btn() + " px-4 py-2 text-sm"}>{autoBusy ? "Enabling…" : "Enable auto-accept"}</button>
            </div>
          </div>
        </Modal>
      )}

      {(friendlyScrims.incoming.some((c) => c.status === "pending") || friendlyScrims.outgoing.length > 0) && (
        <div className="mb-8" data-testid="friendly-scrim-section">
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Friendly Scrimmages</h2>
          <div className="space-y-2">
            {friendlyScrims.incoming.filter((c) => c.status === "pending").map((c) => (
              <div key={c.id} data-testid={`fs-incoming-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-zinc-950 border border-zinc-800">
                <div className="text-sm">
                  <span className="text-zinc-300">{c.from_name}</span>'s <span className="text-amber-300">{c.from_glad_name}</span>
                  <span className="text-zinc-500"> invites you to a friendly scrimmage</span>
                  <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${STATUS_COLOR.pending}`}>pending</span>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`fs-accept-${c.id}`} className={btn() + " py-1.5 px-3"} onClick={() => { setAcceptFS(c); setAfGlad(""); setAfTactics("defensive"); }}>Accept</button>
                  <button data-testid={`fs-decline-${c.id}`} className={btn("danger") + " py-1.5 px-3"} onClick={() => declineFriendly(c.id)}>Decline</button>
                </div>
              </div>
            ))}
            {friendlyScrims.outgoing.map((c) => (
              <div key={c.id} data-testid={`fs-outgoing-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-zinc-950 border border-zinc-800">
                <div className="text-sm">
                  Your <span className="text-amber-300">{c.from_glad_name}</span><span className="text-zinc-500"> vs </span>
                  <span className="text-amber-300">{c.to_glad_name || c.to_name}</span>
                  <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${STATUS_COLOR[c.status]}`}>{c.status}</span>
                </div>
                {c.status === "completed" && c.battle_id && (
                  <button className={btn("ghost") + " py-1.5 px-3"} onClick={() => viewBattle(c.battle_id)}>View Battle</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Incoming</h2>
      {orderedIncomingChallenges.length === 0 ? <Empty label="No incoming challenges." /> : (
        <div className="mb-8">
          <div className="space-y-2">
            {orderedIncomingChallenges.slice((inPage - 1) * PER_PAGE, inPage * PER_PAGE).map((c) => (
              <div key={c.id} data-testid={`incoming-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-zinc-950 border border-zinc-800">
                <div className="text-sm flex flex-wrap items-center gap-x-1 gap-y-1">
                  {c.other_sigil && <Sigil id={c.other_sigil} size={18} className="mr-0.5" />}
                  <span className="text-zinc-300">{c.challenger_name}</span>'s{" "}
                  <button data-testid={`incoming-challenger-glad-${c.id}`} onClick={() => navigate(`/gladiator/${c.challenger_glad}`)}
                    className="text-amber-300 hover:text-amber-200 hover:underline">{c.challenger_glad_name}</button>
                  <span className="text-zinc-500"> challenges your </span>
                  <button data-testid={`incoming-opponent-glad-${c.id}`} onClick={() => navigate(`/gladiator/${c.opponent_glad}`)}
                    className="text-amber-300 hover:text-amber-200 hover:underline">{c.opponent_glad_name}</button>
                  <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${STATUS_COLOR[c.status]}`}>{c.status}</span>
                </div>
                {c.status === "pending" && (
                  <div className="flex gap-2">
                    <button data-testid={`accept-${c.id}`} className={btn() + " py-1.5 px-3"} onClick={() => { setAcceptCh(c); setAccTactics("balanced"); }}>Accept</button>
                    <button data-testid={`decline-${c.id}`} className={btn("danger") + " py-1.5 px-3"} onClick={() => decline(c.id)}>Decline</button>
                  </div>
                )}
                {c.status === "completed" && c.battle_id && (
                  <button className={btn("ghost") + " py-1.5 px-3"} onClick={() => viewBattle(c.battle_id)}>View Battle</button>
                )}
              </div>
            ))}
          </div>
          <Pager page={inPage} total={orderedIncomingChallenges.length} perPage={PER_PAGE} onPage={setInPage} testid="incoming-pager" />
        </div>
      )}

      <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Outgoing</h2>
      {challenges.outgoing.length === 0 ? <Empty label="No outgoing challenges." /> : (
        <div>
          <div className="space-y-2">
            {challenges.outgoing.slice((outPage - 1) * PER_PAGE, outPage * PER_PAGE).map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-zinc-950 border border-zinc-800">
                <div className="text-sm">
                  Your <span className="text-amber-300">{c.challenger_glad_name}</span><span className="text-zinc-500"> vs </span>
                  <span className="text-amber-300">{c.opponent_glad_name}</span>
                  <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${STATUS_COLOR[c.status]}`}>{c.status}</span>
                </div>
                {c.status === "completed" && c.battle_id && (
                  <button className={btn("ghost") + " py-1.5 px-3"} onClick={() => viewBattle(c.battle_id)}>View Battle</button>
                )}
              </div>
            ))}
          </div>
          <Pager page={outPage} total={challenges.outgoing.length} perPage={PER_PAGE} onPage={setOutPage} testid="outgoing-pager" />
        </div>
      )}

      {/* New challenge modal */}
      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Send a Challenge">
        <label className="text-[11px] uppercase tracking-widest text-zinc-500">Your Gladiator</label>
        <select data-testid="my-glad-select" value={myGlad} onChange={(e) => { setMyGlad(e.target.value); setOppGlad(""); }}
          className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-3 text-zinc-200 focus:border-amber-600 outline-none">
          <option value="">— select —</option>
          {mine.map((g) => <option key={g.id} value={g.id} disabled={!g.fight_ready}>{g.name} ({g.cls}, {g.rating}){g.fight_ready ? "" : " — resting"}</option>)}
        </select>
        {myFighter?.rank_bracket && (
          <p data-testid="selected-gladiator-stat-bracket" className="-mt-2 mb-3 text-xs text-amber-300">
            Current ELO division: <span className="font-cinzel">{displayDivision} division</span>
          </p>
        )}

        <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
          <span>Opponent</span>
          {myFighter && <span data-testid="opponent-bracket-filter" className="text-[10px] text-zinc-600 normal-case tracking-normal">{opponentBracket ? `${displayDivision} division · ` : ""}{isLowRated ? `up to ${lowCap}` : `${myRating - range}–${myRating + range}`}</span>}
        </label>
        {!myFighter ? (
          <p className="text-xs text-zinc-600 my-3" data-testid="pick-fighter-hint">Pick one of your gladiators first to see opponents in the same ELO division.</p>
        ) : eligible.length === 0 ? (
          <p className="text-xs text-zinc-600 my-3" data-testid="no-eligible-opponents">No opponents in {opponentBracket ? opponentBracket.replaceAll("_", " ") : "this"} bracket {isLowRated ? `up to ${lowCap} rating` : `within ±${range} rating`} of {myFighter.name} ({myRating}) right now. Check back later.</p>
        ) : (
          <div data-testid="opponent-list" className="mt-1 mb-3 max-h-60 overflow-y-auto border border-zinc-800 divide-y divide-zinc-900">
            {eligible.map((g) => {
              const selected = oppGlad === g.id;
              const ally = g.owner_alliance_id && myAllianceId && g.owner_alliance_id === myAllianceId;
              const rival = g.owner_alliance_id && myRivalId && g.owner_alliance_id === myRivalId;
              const arena = arenaRivals[g.id];
              
              let matchupLabel = null;
              let matchupTone = "neutral";
              let pWin = null;
              if (showMatchupGuidance) {
                // Stable tactic-aware estimate; the opponent is conservatively assumed to use Balanced.
                pWin = g.matchup_estimate ?? 50;
                if (pWin <= 42) {
                  matchupLabel = "Disadvantage";
                  matchupTone = "loss";
                } else if (pWin <= 48) {
                  matchupLabel = "Slight Disadvantage";
                  matchupTone = "loss";
                } else if (pWin <= 52) {
                  matchupLabel = "Even Matchup";
                  matchupTone = "even";
                } else if (pWin <= 58) {
                  matchupLabel = "Slight Advantage";
                  matchupTone = "win";
                } else {
                  matchupLabel = "Advantage";
                  matchupTone = "win";
                }
              }

              const isLikelyWin = matchupTone === "win";
              const isLikelyLoss = matchupTone === "loss";
              const isEvenMatchup = matchupTone === "even";
              const showFirstDefensePromotion = Boolean(
                g.crown_first_defense_pending && (g.bronze_crown || g.silver_crown)
              );
              const promotionDivision = g.bronze_crown ? "Silver Division" : "Gold Division";
              
              let buttonBgClass = "border-l-2 border-transparent hover:bg-zinc-900";
              if (selected) {
                buttonBgClass = "relative z-10 -my-px border border-amber-300 bg-amber-500/20 shadow-[inset_5px_0_0_#fbbf24,inset_0_0_0_1px_rgba(253,230,138,0.5),0_0_18px_rgba(245,158,11,0.22)]";
              } else if (isLikelyWin) {
                buttonBgClass = "border-l-2 border-emerald-600/30 bg-emerald-950/10 hover:bg-emerald-900/20";
              } else if (isLikelyLoss) {
                buttonBgClass = "border-l-2 border-rose-600/30 bg-rose-950/10 hover:bg-rose-900/20";
              }

              return (
                <Fragment key={g.id}>
                  <button type="button" data-testid={`opponent-${g.id}`} disabled={!g.fight_ready} aria-pressed={selected}
                    onClick={() => { setOppGlad(g.id); setComparison(null); }}
                    className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${buttonBgClass}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    {g.owner_online
                      ? <Circle data-testid={`opp-online-${g.id}`} size={8} className="text-emerald-500 fill-emerald-500 shrink-0" title="This lanista is online now — challenge them!" />
                      : <Circle size={8} className="text-zinc-700 shrink-0" />}
                    {g.owner_auto_accept && (
                      <Swords size={12} className="text-amber-400 shrink-0 animate-pulse" data-testid={`opp-auto-accept-${g.id}`} title="Auto-accept enabled — battle will run instantly!" />
                    )}
                    {(g.bronze_crown || g.silver_crown || g.golden_crown) && (
                      <span data-testid={`crown-holder-opponent-${g.id}`} className="inline-flex items-center gap-1.5 shrink-0 border border-amber-500/70 bg-amber-950/40 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-200">
                        <GoldenCrownIcon size={12} crownName={g.bronze_crown ? "Bronze Crown" : g.silver_crown ? "Silver Crown" : "Golden Crown"} />
                        {g.bronze_crown ? "Bronze Crown Holder" : g.silver_crown ? "Silver Crown Holder" : "Golden Crown Holder"}
                      </span>
                    )}
                    <span className="truncate text-sm">
                      <a 
                        href={`/gladiator/${g.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/gladiator/${g.id}`); }}
                        onContextMenu={(e) => e.stopPropagation()}
                        className={`${rival ? "text-red-400 hover:text-red-300" : ally ? "text-sky-400 hover:text-sky-300" : "text-zinc-200 hover:text-amber-300"} hover:underline cursor-pointer`}
                        data-testid={rival ? `opp-rival-name-${g.id}` : ally ? `opp-ally-name-${g.id}` : undefined}
                      >
                        {g.name}
                      </a>
                      <span className={rival ? "text-red-500/70" : ally ? "text-sky-500/70" : "text-zinc-500"}> · {g.owner_name}</span>
                      {rival
                        ? <span data-testid={`opp-rival-tag-${g.id}`} className="ml-1.5 text-[9px] uppercase tracking-wider text-red-400 border border-red-500/40 bg-red-950/30 px-1 py-0.5 rounded-sm align-middle">Rival</span>
                        : ally && <span data-testid={`opp-ally-tag-${g.id}`} className="ml-1.5 text-[9px] uppercase tracking-wider text-sky-400 border border-sky-500/40 bg-sky-950/30 px-1 py-0.5 rounded-sm align-middle">Alliance</span>}
                      {arena && (
                        <span data-testid={`opp-arena-rival-tag-${g.id}`} title={`Arena rival — met ${arena.meetings} times (${arena.wins}W-${arena.losses}L for ${myFighter?.name})`}
                          className="ml-1.5 text-[9px] uppercase tracking-wider text-orange-300 border border-orange-500/50 bg-orange-950/30 px-1 py-0.5 rounded-sm align-middle inline-flex items-center gap-0.5">
                          <Swords size={9} /> Arena Rival {arena.wins}-{arena.losses}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="text-[11px] text-zinc-500 shrink-0">
                    {g.cls} ·{" "}
                    <span className={isLikelyWin ? "text-emerald-400 font-bold" : isLikelyLoss ? "text-rose-400 font-bold" : isEvenMatchup ? "text-amber-300 font-bold" : "text-amber-400"}>
                      {g.rating}
                    </span>
                    {matchupLabel && (
                      <span
                        className={`ml-1.5 font-bold ${isLikelyWin ? "text-emerald-500" : isLikelyLoss ? "text-rose-500" : "text-amber-300"}`}
                        data-testid={`opp-matchup-guidance-${g.id}`}
                        title={`Matchup guidance based on the current combat profile: ${pWin}% estimated win chance.`}
                      >
                        ({matchupLabel})
                      </span>
                    )}
                    {g.fight_ready ? "" : " · resting"}
                  </span>
                  </button>
                  {showFirstDefensePromotion && (
                    <div data-testid={`crown-first-defense-promotion-${g.id}`} className="w-full px-3 py-2 border-y border-amber-900/40 bg-amber-950/20 text-[11px] text-amber-200 flex items-center gap-2">
                      <GoldenCrownIcon size={13} crownName={g.bronze_crown ? "Bronze Crown" : "Silver Crown"} />
                      <span>First crown defense: a successful defense promotes this holder to {promotionDivision}.</span>
                    </div>
                  )}
                </Fragment>
              );
            })}
            {hasMoreOpponents && (
              <button
                type="button"
                data-testid="load-more-opponents"
                disabled={loadingOpponents}
                onClick={handleLoadMoreOpponents}
                className="w-full py-2 bg-zinc-900 text-amber-400 hover:bg-zinc-800 text-xs font-cinzel tracking-wider uppercase transition-colors disabled:opacity-50"
              >
                {loadingOpponents ? "Recruiting opponents..." : "Load More Opponents"}
              </button>
            )}
          </div>
        )}

        <TacticsPicker value={tactics} onChange={setTactics} gladiator={myFighter} />
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <button data-testid="open-arena-comparison-button" disabled={!myGlad || !oppGlad || busy} className={btn("ghost") + " w-full"} onClick={openComparison}>Compare Gladiators</button>
          <button data-testid="send-challenge-btn" disabled={busy} className={btn() + " w-full"} onClick={send}>Send Challenge</button>
        </div>
      </Modal>
      <ArenaComparisonModal open={comparisonOpen} onClose={() => setComparisonOpen(false)} loading={comparisonLoading} comparison={comparison} />

      {/* Accept modal */}
      <Modal open={!!acceptCh} onClose={() => setAcceptCh(null)} title="Accept Challenge">
        {acceptCh && (
          <>
            <p className="text-sm text-zinc-400 mb-2">Set tactics for <span className="text-amber-300">{acceptCh.opponent_glad_name}</span> against {acceptCh.challenger_name}'s {acceptCh.challenger_glad_name}.</p>
            <TacticsPicker value={accTactics} onChange={setAccTactics} gladiator={mine.find(g => g.id === acceptCh.opponent_glad_id)} />
            <button data-testid="confirm-accept-btn" disabled={busy} className={btn() + " w-full mt-2"} onClick={accept}>
              <span className="flex items-center justify-center gap-2"><Shield size={15} /> Fight!</span>
            </button>
          </>
        )}
      </Modal>

      {/* Scrimmage modal */}
      <Modal open={scrimOpen} onClose={() => setScrimOpen(false)} title="Scrimmage" wide>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <button type="button" data-testid="scrim-mode-self" onClick={() => setScrimMode("self")}
            className={`px-2 py-2 text-xs border text-center transition-colors ${scrimMode === "self" ? "border-amber-500 bg-amber-900/20 text-amber-300" : "border-zinc-800 text-zinc-400 hover:border-amber-700/50"}`}>
            <div className="font-cinzel uppercase tracking-wide">Scrimmage</div>
            <div className="text-[10px] text-zinc-600 mt-0.5">Your own two fighters</div>
          </button>
          <button type="button" data-testid="scrim-mode-friendly" onClick={() => setScrimMode("friendly")}
            className={`px-2 py-2 text-xs border text-center transition-colors ${scrimMode === "friendly" ? "border-amber-500 bg-amber-900/20 text-amber-300" : "border-zinc-800 text-zinc-400 hover:border-amber-700/50"}`}>
            <div className="font-cinzel uppercase tracking-wide">Friendly</div>
            <div className="text-[10px] text-zinc-600 mt-0.5">Challenge a friend</div>
          </button>
          <button type="button" data-testid="scrim-mode-prolusio" onClick={() => setScrimMode("prolusio")}
            className={`px-2 py-2 text-xs border text-center transition-colors ${scrimMode === "prolusio" ? "border-amber-500 bg-amber-900/20 text-amber-300" : "border-zinc-800 text-zinc-400 hover:border-amber-700/50"}`}>
            <div className="font-cinzel uppercase tracking-wide">Prolusio</div>
            <div className="text-[10px] text-zinc-600 mt-0.5">AI specialists (PvE)</div>
          </button>
        </div>
        {scrimMode !== "prolusio" && (
          <p className="text-xs mb-4"><span className="text-amber-400" data-testid="scrim-remaining">Scrimmages left today: {limits?.scrimmages_remaining ?? "…"}/3</span></p>
        )}

        {scrimMode === "self" && (
          <>
            <p className="text-sm text-zinc-400 mb-3">A friendly exhibition between two of your own fighters. Just for sport — no ratings, records, or fight cooldowns change.</p>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Red Corner</label>
            <select data-testid="scrim-g1-select" value={sg1} onChange={(e) => setSg1(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-2 text-zinc-200 focus:border-amber-600 outline-none">
              <option value="">— select —</option>
              {mine.map((g) => <option key={g.id} value={g.id} disabled={g.id === sg2}>{g.name} ({g.cls}, {g.rating})</option>)}
            </select>
            <TacticsPicker value={st1} onChange={setSt1} gladiator={mine.find(g => g.id === sg1)} />

            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Blue Corner</label>
            <select data-testid="scrim-g2-select" value={sg2} onChange={(e) => setSg2(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-2 text-zinc-200 focus:border-amber-600 outline-none">
              <option value="">— select —</option>
              {mine.map((g) => <option key={g.id} value={g.id} disabled={g.id === sg1}>{g.name} ({g.cls}, {g.rating})</option>)}
            </select>
            <TacticsPicker value={st2} onChange={setSt2} gladiator={mine.find(g => g.id === sg2)} />

            {mine.length < 2 && <p className="text-xs text-zinc-600 mb-2">You need at least 2 active gladiators to scrimmage.</p>}
            <button data-testid="run-scrimmage-btn" disabled={busy || mine.length < 2 || (limits?.scrimmages_remaining === 0)} className={btn() + " w-full mt-2"} onClick={runScrimmage}>
              <span className="flex items-center justify-center gap-2"><Swords size={15} /> {limits?.scrimmages_remaining === 0 ? "Daily Limit Reached" : "Fight for Sport"}</span>
            </button>
          </>
        )}

        {scrimMode === "friendly" && (
          <>
            <p className="text-sm text-zinc-400 mb-3">Invite a friend to a friendly exhibition. They choose their fighter and must accept. Just for sport — no ratings or cooldowns change. Sending reserves one of your daily scrimmages (refunded if declined).</p>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Your Gladiator</label>
            <select data-testid="fs-glad-select" value={fsGlad} onChange={(e) => setFsGlad(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-3 text-zinc-200 focus:border-amber-600 outline-none">
              <option value="">— select —</option>
              {mine.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.cls}, {g.rating})</option>)}
            </select>

            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Friend</label>
            {friends.length === 0 ? (
              <p className="text-xs text-zinc-600 my-3" data-testid="no-friends-hint">You have no friends yet. Add friends from their profile to send friendly scrimmages.</p>
            ) : (
              <select data-testid="fs-friend-select" value={fsFriend} onChange={(e) => setFsFriend(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-2 text-zinc-200 focus:border-amber-600 outline-none">
                <option value="">— select —</option>
                {friends.map((f) => <option key={f.user_id} value={f.user_id}>{f.name}{f.online ? " (online)" : ""}</option>)}
              </select>
            )}
            <TacticsPicker value={fsTactics} onChange={setFsTactics} gladiator={mine.find(g => g.id === fsGlad)} />

            {mine.length < 1 && <p className="text-xs text-zinc-600 mb-2">You need an active gladiator to scrimmage.</p>}
            <button data-testid="send-friendly-btn" disabled={busy || mine.length < 1 || friends.length === 0 || (limits?.scrimmages_remaining === 0)} className={btn() + " w-full mt-2"} onClick={sendFriendly}>
              <span className="flex items-center justify-center gap-2"><Swords size={15} /> {limits?.scrimmages_remaining === 0 ? "Daily Limit Reached" : "Send Friendly Scrimmage"}</span>
            </button>
          </>
        )}

        {scrimMode === "prolusio" && (
          <>
            <p className="text-sm text-zinc-400 mb-3">Exhibition bouts against escalating arena specialists — no rating, no record, no cooldown. Win to earn both fragments AND denarii, then unlock the next challenger. Each gladiator gets {prolusio?.daily_per_gladiator ?? 1} Prolusio bout{(prolusio?.daily_per_gladiator ?? 1) === 1 ? "" : "s"} per day.</p>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Your Gladiator</label>
            <select data-testid="prolusio-glad-select" value={pGlad} onChange={(e) => setPGlad(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-2 text-zinc-200 focus:border-amber-600 outline-none">
              <option value="">— select —</option>
              {mine.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.cls}, {g.rating})</option>)}
            </select>
            {pGlad && prolusio?.remaining != null && (
              <p data-testid="prolusio-remaining" className={`text-xs mb-3 ${prolusio.remaining === 0 ? "text-red-400" : "text-amber-400"}`}>
                Prolusio bouts left today for this gladiator: {prolusio.remaining}/{prolusio.daily_per_gladiator}
              </p>
            )}
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Opponent</label>
            <div data-testid="prolusio-tier-list" className="mt-1 mb-3 max-h-72 overflow-y-auto border border-zinc-800 divide-y divide-zinc-900">
              {(prolusio?.tiers || []).map((t) => {
                const locked = !t.unlocked;
                const selected = pTier === t.tier;
                return (
                  <button key={t.tier} type="button" data-testid={`prolusio-tier-${t.tier}`} disabled={locked}
                    onClick={() => setPTier(t.tier)}
                    className={`w-full text-left px-3 py-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${selected ? "bg-amber-900/20 border-l-2 border-amber-500" : "border-l-2 border-transparent hover:bg-zinc-900"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-zinc-200 flex items-center gap-1.5">
                        <span className="text-zinc-500 tabular-nums">#{t.tier}</span> {t.name}
                        {t.beaten && <Check size={13} className="text-emerald-500" title="Defeated" />}
                        {locked && <Lock size={12} className="text-zinc-600" />}
                      </span>
                      <span className="text-[11px] text-zinc-500 shrink-0">{t.cls} · Power <span className="text-amber-400">{t.avg}</span> · {t.tactics}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 italic mt-0.5">{t.archetype}</div>
                    <div className="text-[10px] text-zinc-600 mt-1 flex items-center gap-2">
                      <span className="flex items-center gap-1"><Sparkles size={9} className="text-amber-500/70" /> {t.reward.frags} frag{t.reward.frags > 1 ? "s" : ""}</span>
                      <span className="text-zinc-700">or</span>
                      <span className="flex items-center gap-1"><HandCoins size={9} className="text-slate-400" /> {t.reward.denarii} denarii</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <TacticsPicker value={pTactics} onChange={setPTactics} gladiator={mine.find(g => g.id === pGlad)} />
            <button data-testid="run-prolusio-btn" disabled={busy || !pGlad || !pTier || prolusio?.remaining === 0} className={btn() + " w-full mt-2"} onClick={runProlusio}>
              <span className="flex items-center justify-center gap-2"><Swords size={15} /> {prolusio?.remaining === 0 ? "Daily Limit Reached" : "Enter the Prolusio"}</span>
            </button>
          </>
        )}
      </Modal>

      {/* Accept friendly scrimmage modal */}
      <Modal open={!!acceptFS} onClose={() => setAcceptFS(null)} title="Accept Friendly Scrimmage">
        {acceptFS && (
          <>
            <p className="text-sm text-zinc-400 mb-3">{acceptFS.from_name}'s <span className="text-amber-300">{acceptFS.from_glad_name}</span> wants a friendly exhibition. Pick your fighter — no ratings or cooldowns change.</p>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Your Gladiator</label>
            <select data-testid="af-glad-select" value={afGlad} onChange={(e) => setAfGlad(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-2 text-zinc-200 focus:border-amber-600 outline-none">
              <option value="">— select —</option>
              {mine.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.cls}, {g.rating})</option>)}
            </select>
            <TacticsPicker value={afTactics} onChange={setAfTactics} gladiator={mine.find(g => g.id === afGlad)} />
            {mine.length < 1 && <p className="text-xs text-zinc-600 mb-2">You need an active gladiator to accept.</p>}
            <button data-testid="confirm-friendly-btn" disabled={busy || !afGlad} className={btn() + " w-full mt-2"} onClick={acceptFriendly}>
              <span className="flex items-center justify-center gap-2"><Shield size={15} /> Fight for Sport!</span>
            </button>
          </>
        )}
      </Modal>

      {/* Battle play-by-play (animated, skippable) */}
      <BattleReplay
        battle={battle}
        open={!!battle}
        onClose={() => { setBattle(null); setPClaim(null); setBattleInlineNotices([]); }}
        title={battle?.__prolusio ? "Prolusio Report" : battle?.exhibition ? "Scrimmage Report" : "Battle Report"}
        exhibition={battle?.exhibition}
        inlineNotices={battleInlineNotices}
        rewards={
          battle?.__prolusio
            ? (battle.won && pClaim ? <ProlusioReward claim={pClaim} newlyUnlocked={pUnlocked} /> : null)
            : (battle && !battle.exhibition ? <SalvageChips
                rew={battle.your_rewards || (battle.salvage && user && battle.salvage[user.user_id])}
                dailyProgress={battle.your_daily_arena_fragment_progress || (battle.daily_arena_fragment_progress && user && battle.daily_arena_fragment_progress[user.user_id])}
              /> : null)
        }
        shareable={!!battle && !battle.exhibition}
      />
    </div>
  );
}
